// src/components/common/CustomerUserDropdownMenu.jsx
import React, { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { cn } from '@/lib/utils';

import { logoutAuthService } from '@/services/AuthServices';
import { getFileUrl } from '@/services/CommonService';
import {
  updateMyProfilePhotoService,   // self-service photo API
  getCustomerProfileService,     // re-fetch after upload
} from '@/services/CustomerServices';
import { setUserData } from '@/store/actions';
import validationErrors from '@/lib/validationErrors';
import ToastMe from '@/components/ui/ToastMe';

const CustomerUserDropdownMenu = () => {
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const imageRef  = useRef(null);

  // In some apps userData default is '' (string). Normalize to object.
  const rawUser = useSelector((s) => s.Auth.userData);
  const customer = rawUser && typeof rawUser === 'object' ? rawUser : {};

  const [isLoading, setIsLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  /* ──────────────────────────────── */
  /*  Avatar upload                   */
  /* ──────────────────────────────── */
  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file?.type?.startsWith('image/')) {
      ToastMe('Please upload a valid image', 'error');
      return;
    }

    const form = new FormData();
    // middleware expects the field name "photo"
    form.append('photo', file);

    setIsLoading(true);
    dispatch(updateMyProfilePhotoService(form))
      .then((res) => {
        ToastMe(res?.data?.message || 'Photo updated', 'success');
        // Re-hydrate Redux with the latest customer so avatar persists on refresh
        return dispatch(getCustomerProfileService());
      })
      .then((res) => {
        const data = res?.data?.data;
        if (data) dispatch(setUserData({ data: { data } }));
      })
      .catch(({ message, errorData }) => validationErrors(errorData, message))
      .finally(() => setIsLoading(false));
  };

  /* ──────────────────────────────── */
  /*  Logout                          */
  /* ──────────────────────────────── */
  const handleLogout = () => {
    setIsLoading(true);
    dispatch(logoutAuthService()).finally(() => {
      setIsLoading(false);
      navigate('/menu');
    });
  };

  /* ──────────────────────────────── */
  /*  Avatar src with cache-busting   */
  /* ──────────────────────────────── */
// ...
  // Prefer updatedAt from the DB; fallback keeps cache fresh after hard reload
  const updatedAt =
    customer?.updatedAt || customer?.updated_at || customer?.modifiedAt || null;
  const version = updatedAt ? new Date(updatedAt).getTime() : Date.now();

  const avatarSrc = customer?.file_name
    ? `${getFileUrl(customer.file_name, 'profile')}?v=${version}`
    : '/assets/images/default/profile-image.jpg';
// ...

  return (
    <div className="dropdown-group">
      {/* ─── toggle button ─── */}
      <button
        disabled={isLoading}
        onClick={() => setDropdownOpen((o) => !o)}
        className="dropdown-btn flex items-center gap-2"
      >
        <img className="w-9 h-9 rounded-lg object-cover" src={avatarSrc} alt="avatar" />
        <h3 className="text-sm leading-[17px] text-left">
          Hello&nbsp;
          <b className="block font-semibold capitalize">
            {customer?.name ?? 'Guest'}
          </b>
        </h3>
        <i className="lab lab-arrow-down text-xs ml-1.5" />
      </button>

      {/* ─── dropdown ─── */}
      <div
        className={cn(
          'dropdown-list fixed sm:absolute top-[75px] sm:top-12 right-0 z-50 rounded-xl w-full sm:w-[360px] p-4 bg-white shadow-paper',
          dropdownOpen ? 'active' : 'hidden'
        )}
      >
        {/* profile header */}
        <div className="text-center mb-5">
          <figure className="relative inline-block w-[98px] h-[98px] border-2 border-dashed rounded-full mx-auto bg-gradient-to-t from-[#FF7A00] to-[#FF016C]">
            <img
              className="w-[90px] h-[90px] rounded-full shadow-avatar mx-auto mt-[4px]"
              src={avatarSrc}
              alt="avatar"
            />
          </figure>

          {/* upload button */}
          <label
            htmlFor="customer-avatar"
            className="block w-11 h-11 mx-auto -mt-7 mb-3 rounded-full border-2 border-white bg-heading cursor-pointer relative"
          >
            <input
              id="customer-avatar"
              type="file"
              accept="image/*"
              ref={imageRef}
              onChange={handleFileUpload}
              className="w-full h-full opacity-0 cursor-pointer"
            />
            <i className="lab lab-edit-2 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-2xl text-white" />
          </label>

          {/* customer info */}
          <h3 className="font-medium text-sm leading-6 capitalize mb-0.5">
            {customer?.name ?? '-'}
          </h3>
          <p className="text-xs mb-0.5">{customer?.email ?? '-'}</p>
          <p dir="ltr" className="text-xs">
            +{customer?.phone_no ?? '-'}
          </p>
        </div>

        {/* nav links */}
        <nav>
          <Link
            to="/customer/profile"
            className="flex items-center gap-3.5 py-3 border-b border-[#EFF0F6] text-sm capitalize"
          >
            <i className="lab lab-edit text-lg" /> Edit Profile
          </Link>
          <Link
            to="/customer/change-password"
            className="flex items-center gap-3.5 py-3 border-b border-[#EFF0F6] text-sm capitalize"
          >
            <i className="lab lab-key text-lg" /> Change Password
          </Link>
          <button
            type="button"
            onClick={handleLogout}
            className="flex items-center gap-3.5 py-3 w-full text-left text-sm capitalize"
          >
            <i className="lab lab-logout text-lg" /> Logout
          </button>
        </nav>
      </div>
    </div>
  );
};

export default CustomerUserDropdownMenu;
